import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  GraduationCap, CheckCircle, AlertCircle, Loader2,
  ChevronDown, MapPin, Phone, Mail, Globe, ArrowLeft
} from 'lucide-react';
import api from '../api';

const CLASSES = [
  'Creche', 'Nursery 1', 'Nursery 2', 'KG 1', 'KG 2',
  'Primary 1', 'Primary 2', 'Primary 3', 'Primary 4', 'Primary 5', 'Primary 6',
  'JSS 1', 'JSS 2', 'JSS 3', 'SS 1', 'SS 2', 'SS 3',
];

const INIT = {
  first_name: '', last_name: '', date_of_birth: '', gender: '',
  applying_for_class: '', previous_school: '',
  guardian_name: '', guardian_relationship: 'Parent',
  guardian_phone: '', guardian_email: '', address: '', notes: '',
};

export default function ApplySchool() {
  const { slug } = useParams();
  const [school, setSchool] = useState(null);
  const [loadingSchool, setLoadingSchool] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.get(`/admissions/school/${slug}`)
      .then(res => setSchool(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoadingSchool(false));
  }, [slug]);

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.last_name.trim()) e.last_name = 'Required';
    if (!form.date_of_birth) e.date_of_birth = 'Required';
    if (!form.gender) e.gender = 'Required';
    if (!form.applying_for_class) e.applying_for_class = 'Required';
    if (!form.guardian_name.trim()) e.guardian_name = 'Required';
    if (!form.guardian_phone.trim()) e.guardian_phone = 'Required';
    if (!form.guardian_email.trim()) e.guardian_email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.guardian_email)) e.guardian_email = 'Enter a valid email';
    return e;
  };

  const sel = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.post(`/admissions/apply-school/${slug}`, form);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErrors({ _global: err?.detail || 'Submission failed. Please try again.' });
    } finally { setSubmitting(false); }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loadingSchool) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
      </div>
    );
  }

  // ── 404 state ─────────────────────────────────────────────────────────────
  if (notFound || !school) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-8 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <GraduationCap className="w-8 h-8 text-slate-300" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Portal Not Found</h2>
        <p className="text-slate-500 mb-6 max-w-sm">
          This school's admission portal is not available. Please check the link or contact the school directly.
        </p>
        <a href="/" className="flex items-center gap-1.5 text-sm font-semibold text-[#2563eb] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Go back to EDUVA
        </a>
      </div>
    );
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)' }}>
        <SchoolHeader school={school} />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center border border-slate-100">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900 mb-3">Application Submitted!</h2>
            <p className="text-slate-600 mb-4 leading-relaxed">
              Thank you for applying to <strong>{school.name}</strong>. Your application is now under review.
            </p>
            <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">What happens next?</p>
              <ul className="text-sm text-slate-600 space-y-1.5">
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> The school has received your application</li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> You'll be contacted at <strong>{form.guardian_email}</strong></li>
                <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">✓</span> Processing typically takes 3–5 working days</li>
              </ul>
            </div>
            <button onClick={() => { setForm(INIT); setSuccess(false); setErrors({}); }}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all text-white"
              style={{ background: school.primary_color || '#2563eb' }}>
              Submit Another Application
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const accent = school.primary_color || '#2563eb';

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)' }}>
      <SchoolHeader school={school} accent={accent} />

      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-8">
        {/* Form intro */}
        <div className="text-center mb-7">
          <h2 className="text-xl font-extrabold text-slate-900">Online Admission Application</h2>
          <p className="text-sm text-slate-500 mt-1">All fields marked <span className="text-red-500">*</span> are required</p>
        </div>

        {errors._global && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-5 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{errors._global}</p>
          </div>
        )}

        <form onSubmit={submit} data-testid="apply-school-form" className="space-y-5">
          {/* Student Information */}
          <Card title="Student Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="First Name *" error={errors.first_name}>
                <input type="text" value={form.first_name} onChange={e => sel('first_name', e.target.value)}
                  className={inp(errors.first_name)} placeholder="First name"
                  data-testid="apply-first-name" />
              </Field>
              <Field label="Last Name *" error={errors.last_name}>
                <input type="text" value={form.last_name} onChange={e => sel('last_name', e.target.value)}
                  className={inp(errors.last_name)} placeholder="Last name"
                  data-testid="apply-last-name" />
              </Field>
              <Field label="Date of Birth *" error={errors.date_of_birth}>
                <input type="date" value={form.date_of_birth} onChange={e => sel('date_of_birth', e.target.value)}
                  className={inp(errors.date_of_birth)} data-testid="apply-dob" />
              </Field>
              <Field label="Gender *" error={errors.gender}>
                <Select value={form.gender} onChange={v => sel('gender', v)} error={errors.gender} testId="apply-gender">
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </Select>
              </Field>
              <Field label="Class Applying For *" error={errors.applying_for_class}>
                <Select value={form.applying_for_class} onChange={v => sel('applying_for_class', v)} error={errors.applying_for_class} testId="apply-class">
                  <option value="">Select class...</option>
                  {CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label="Previous School">
                <input type="text" value={form.previous_school} onChange={e => sel('previous_school', e.target.value)}
                  className={inp()} placeholder="Previous school (optional)" />
              </Field>
            </div>
          </Card>

          {/* Guardian Information */}
          <Card title="Parent / Guardian Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name *" error={errors.guardian_name}>
                <input type="text" value={form.guardian_name} onChange={e => sel('guardian_name', e.target.value)}
                  className={inp(errors.guardian_name)} placeholder="Full name"
                  data-testid="apply-guardian-name" />
              </Field>
              <Field label="Relationship">
                <Select value={form.guardian_relationship} onChange={v => sel('guardian_relationship', v)}>
                  <option value="Parent">Parent</option>
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Sponsor">Sponsor</option>
                </Select>
              </Field>
              <Field label="Phone Number *" error={errors.guardian_phone}>
                <input type="tel" value={form.guardian_phone} onChange={e => sel('guardian_phone', e.target.value)}
                  className={inp(errors.guardian_phone)} placeholder="e.g. 08012345678"
                  data-testid="apply-guardian-phone" />
              </Field>
              <Field label="Email Address *" error={errors.guardian_email}>
                <input type="email" value={form.guardian_email} onChange={e => sel('guardian_email', e.target.value)}
                  className={inp(errors.guardian_email)} placeholder="email@example.com"
                  data-testid="apply-guardian-email" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Home Address">
                  <input type="text" value={form.address} onChange={e => sel('address', e.target.value)}
                    className={inp()} placeholder="Full home address" />
                </Field>
              </div>
            </div>
          </Card>

          {/* Notes */}
          <Card title="Additional Information (Optional)">
            <textarea value={form.notes} onChange={e => sel('notes', e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 transition-colors resize-none h-24"
              placeholder="Any additional information for the school..." />
          </Card>

          <button type="submit" disabled={submitting}
            className="w-full py-3.5 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg active:scale-[0.99] disabled:opacity-60"
            style={{ background: accent }}
            data-testid="submit-apply-btn">
            {submitting
              ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting application...</>
              : 'Submit Application'}
          </button>

          <p className="text-center text-xs text-slate-400 pb-4">
            By submitting, you confirm all information provided is accurate.
            Powered by <span className="font-semibold text-slate-500">EDUVA</span>
          </p>
        </form>
      </div>

      <Footer />
    </div>
  );
}

// ── School branded header ─────────────────────────────────────────────────────
function SchoolHeader({ school, accent }) {
  const bg = accent || school.primary_color || '#1e3a5f';
  return (
    <header className="relative overflow-hidden" style={{ background: bg }}>
      {/* Decorative circles */}
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10"
        style={{ background: 'white', transform: 'translate(30%, -40%)' }} />
      <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full opacity-10"
        style={{ background: 'white', transform: 'translate(-30%, 40%)' }} />

      <div className="relative max-w-2xl mx-auto px-6 py-8 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          {school.logo
            ? <img src={school.logo} alt={school.name}
                className="w-20 h-20 rounded-full object-cover border-4 border-white/30 shadow-lg" />
            : <div className="w-20 h-20 rounded-full bg-white/20 border-4 border-white/30 flex items-center justify-center shadow-lg backdrop-blur-sm">
                <GraduationCap className="w-10 h-10 text-white" />
              </div>
          }
        </div>

        {/* Name + Motto */}
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-tight">
          {school.name}
        </h1>
        {school.motto && (
          <p className="text-white/70 text-sm italic mt-1.5 font-medium">"{school.motto}"</p>
        )}

        {/* Address info */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
          {school.address && (
            <span className="flex items-center gap-1.5 text-white/75 text-xs font-medium">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              {school.city ? `${school.city}, ${school.state}` : school.address}
            </span>
          )}
          {school.phone && (
            <span className="flex items-center gap-1.5 text-white/75 text-xs font-medium">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" /> {school.phone}
            </span>
          )}
          {school.email && (
            <span className="flex items-center gap-1.5 text-white/75 text-xs font-medium">
              <Mail className="w-3.5 h-3.5 flex-shrink-0" /> {school.email}
            </span>
          )}
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mt-4 text-white text-xs font-semibold">
          <GraduationCap className="w-3.5 h-3.5" /> Admission Application Portal
        </div>
      </div>
    </header>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="text-center py-6 text-xs text-slate-400">
      Powered by <a href="https://myeduva.com" className="font-semibold text-slate-500 hover:text-[#2563eb] transition-colors">EDUVA</a> — School Management Platform
    </footer>
  );
}

// ── Reusable form primitives ──────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="bg-slate-50 border-b border-slate-100 px-5 py-3">
        <h3 className="font-bold text-slate-700 text-xs uppercase tracking-widest">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className={`block text-xs font-semibold mb-1.5 ${error ? 'text-red-500' : 'text-slate-600'}`}>{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function Select({ value, onChange, error, testId, children }) {
  return (
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
        className={`w-full border rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-blue-400 appearance-none pr-10 transition-colors ${error ? 'border-red-300' : 'border-slate-200'}`}
        data-testid={testId}>
        {children}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
    </div>
  );
}

function inp(error) {
  return `w-full border rounded-xl px-4 py-3 text-sm bg-white outline-none focus:border-blue-400 transition-colors ${error ? 'border-red-300 focus:border-red-400' : 'border-slate-200'}`;
}
